import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { EinField, PhoneField } from "@/components/common/masked-form-field";
import { SelectField } from "@/features/clients/components/record-form-fields";
import { otherBusinessesApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { ENTITY_TYPE_OPTIONS, type OtherBusiness } from "@/features/worksheets/types";

interface Values {
  name: string;
  percent: string;
  title: string;
  phone: string;
  ein: string;
  entityType: string;
  address: string;
}

const schema = Yup.object({
  name: Yup.string().trim().required("Business name is required"),
  percent: Yup.string()
    .matches(/^\d*(\.\d{0,2})?$/, "Enter a percentage")
    .test("range", "0–100", (v) => !v || (Number(v) >= 0 && Number(v) <= 100)),
});

interface Props {
  caseId: string;
  record: OtherBusiness | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function OtherBusinessDialog({ caseId, record, open, onOpenChange, onSaved }: Props) {
  const initialValues: Values = {
    name: record?.name ?? "",
    percent: record?.percent ?? "",
    title: record?.title ?? "",
    phone: record?.phone ?? "",
    ein: "",
    entityType: record?.entityType ?? "",
    address: record?.address ?? "",
  };

  async function onSave(v: Values) {
    const payload: Record<string, unknown> = {
      name: v.name.trim(),
      percent: v.percent || "0",
      title: v.title,
      phone: v.phone,
      entityType: v.entityType || "",
      address: v.address,
    };
    if (v.ein.trim()) payload.ein = v.ein;
    if (record) await otherBusinessesApi.update(caseId, record.id, payload);
    else await otherBusinessesApi.create(caseId, payload);
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Business interest"
      initialValues={initialValues}
      validationSchema={schema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField name="name" label="Business name" />
        <FormField name="percent" label="Ownership %" inputMode="decimal" placeholder="0" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField name="title" label="Your title" />
        <SelectField name="entityType" label="Entity type" options={ENTITY_TYPE_OPTIONS} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <EinField
          name="ein"
          label="EIN"
          labelAdornment={
            record?.einLabel ? (
              <span className="text-muted-foreground text-xs">On file: {record.einLabel}</span>
            ) : undefined
          }
        />
        <PhoneField name="phone" label="Phone" />
      </div>
      <FormField name="address" label="Address" />
    </CrudDialog>
  );
}
